import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../components/Button";
import Input, { MaterialInputEvent } from "../../../components/v2/Input";
import Modal from "../../../components/v2/Modal";
import { AdminUserErrors, userErrors } from "../../../services/AdminUsersService";
import AuthService from "../../../services/AuthService";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.account.password";
const MIN_PASSWORD = 6;

interface Props {
  open: boolean;
  onClose(): void;
  onChanged(): void;
}

export default function PasswordDialog({ open, onClose, onChanged }: Props) {
  const { t } = useTranslation();

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<AdminUserErrors>({});
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCurrentPassword("");
    setPassword("");
    setPasswordConfirmation("");
    setShowPassword(false);
    setErrors({});
    setFailed(false);
    setSaving(false);
  }, [open]);

  if (!open) {
    return null;
  }

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const errorText = (field: string) => {
    const codes = errors[field];
    if (!codes || codes.length === 0) {
      return undefined;
    }
    return t(`${keyI18n}.errors.${field}.${codes[0]}`, { defaultValue: t(`${keyI18n}.errors.invalid`), min: MIN_PASSWORD });
  };

  const validate = (): AdminUserErrors => {
    const found: AdminUserErrors = {};
    if (!currentPassword) found.currentPassword = ["blank"];
    if (!password) found.password = ["blank"];
    else if (password.length < MIN_PASSWORD) found.password = ["too_short"];
    else if (password === currentPassword) found.password = ["same"];
    if (password && password !== passwordConfirmation) found.passwordConfirmation = ["confirmation"];
    return found;
  };

  const submit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (saving) {
      return;
    }

    const found = validate();
    setErrors(found);
    setFailed(false);
    if (Object.keys(found).length > 0) {
      return;
    }

    setSaving(true);
    AuthService.changePassword({ currentPassword, password, passwordConfirmation })
      .then(() => onChanged())
      .catch((error: unknown) => {
        const serverErrors = userErrors(error);
        if (serverErrors) {
          setErrors(serverErrors);
        } else {
          setFailed(true);
        }
        setSaving(false);
      });
  };

  const visibilityButton = (
    <Button
      iconButton
      variant="text"
      size="extra-small"
      type="button"
      aria-label={t(`${keyI18n}.${showPassword ? "hidePassword" : "showPassword"}`)}
      onClick={() => setShowPassword((value) => !value)}
      icon={<span className="material-symbols-sharp">{showPassword ? "visibility_off" : "visibility"}</span>}
    />
  );

  const fields: { key: string; value: string; autoComplete: string; onChange(value: string): void; hint?: string }[] = [
    { key: "currentPassword", value: currentPassword, autoComplete: "current-password", onChange: setCurrentPassword },
    {
      key: "password",
      value: password,
      autoComplete: "new-password",
      onChange: setPassword,
      hint: t(`${keyI18n}.passwordHint`, { min: MIN_PASSWORD }),
    },
    { key: "passwordConfirmation", value: passwordConfirmation, autoComplete: "new-password", onChange: setPasswordConfirmation },
  ];

  return (
    <Modal
      open
      onClose={close}
      dismissible={!saving}
      icon="lock_reset"
      title={t(`${keyI18n}.title`)}
      size="small"
      actions={(
        <>
          <Button variant="text" size="small" onClick={close} disabled={saving}>
            {t(`${keyI18n}.cancel`)}
          </Button>
          <Button variant="filled" size="small" onClick={() => submit()} disabled={saving} softDisabled={saving}>
            {t(`${keyI18n}.${saving ? "saving" : "save"}`)}
          </Button>
        </>
      )}
    >
      <form className={style.passwordForm} noValidate onSubmit={submit}>
        <p className={`pgm-body-medium ${style.passwordIntro}`}>{t(`${keyI18n}.intro`)}</p>
        {fields.map((field, index) => (
          <div key={field.key} className={style.passwordField}>
            <Input
              variant="outlined"
              type={showPassword ? "text" : "password"}
              label={t(`${keyI18n}.${field.key}`)}
              value={field.value}
              required
              autoFocus={index === 0}
              autoComplete={field.autoComplete}
              fullWidth
              hideClearButton
              trailingIcon={visibilityButton}
              supportingText={field.hint}
              error={Boolean(errorText(field.key))}
              errorText={errorText(field.key)}
              onInput={(event: MaterialInputEvent) => field.onChange(event.currentTarget.value)}
            />
          </div>
        ))}
        {failed && (
          <p className={`pgm-body-medium ${style.passwordError}`} role="alert">
            {t(`${keyI18n}.errors.generic`)}
          </p>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
